import { useQuery } from "@tanstack/react-query";
import { PlugZap } from "lucide-react";

import { orpc } from "@/client";
import type { IntegrationHealth } from "@/api/db/integration-health";
import { EmptyState } from "@/components/ui/empty-state";
import { ToneChip } from "@/components/medico/tone-chip";
import { SkeletonRows } from "@/components/ui/skeleton";
import { SystemLabels } from "@/lib/labels/system";

import { SectionCard } from "./section-card";

export function IntegrationsSection() {
	const { data, isLoading, isError } = useQuery(orpc.system.integrations.queryOptions());

	return (
		<SectionCard title={SystemLabels.sections.integrations} dataComponent="system-integrations">
			{isLoading && <SkeletonRows rows={5} />}

			{!isLoading && isError && (
				<p className="text-sm text-destructive">{SystemLabels.loadError}</p>
			)}

			{!isLoading && !isError && data !== undefined && data.length === 0 && (
				<EmptyState icon={PlugZap} title={SystemLabels.empty.integrations} />
			)}

			{!isLoading && !isError && data !== undefined && data.length > 0 && (
				<ul className="flex flex-col divide-y divide-border">
					{data.map((row) => (
						<IntegrationRow key={row.integration} row={row} />
					))}
				</ul>
			)}
		</SectionCard>
	);
}

function IntegrationRow({ row }: { row: IntegrationHealth }) {
	const recovery = SystemLabels.recovery(row.status);

	return (
		<li
			data-slot="integration"
			data-integration={row.integration}
			data-status={row.status}
			className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0"
		>
			<div className="flex items-start gap-2">
				<div className="flex flex-col">
					<span className="text-sm font-medium text-foreground">
						{SystemLabels.integrationName(row.integration)}
					</span>
					<span className="text-xs text-muted-foreground">
						{SystemLabels.integrationSource(row.integration)}
					</span>
				</div>

				<ToneChip tone={SystemLabels.statusTone[row.status]}>
					{SystemLabels.statusWord[row.status]}
				</ToneChip>
			</div>

			<p className="text-xs text-muted-foreground">{SystemLabels.lastActivity(row.last_ok)}</p>

			{recovery !== null && (
				<p data-slot="recovery" className="text-xs font-medium text-warning">
					{recovery}
				</p>
			)}
		</li>
	);
}
